/**
 * MobileSimulator Component
 * 
 * Displays a mobile app in a phone simulator frame.
 * Can embed either an iframe (for Expo web), render content directly,
 * or show a slideshow of static screenshots.
 * 
 * Usage:
 * ```tsx
 * // Live iframe (requires Expo dev server)
 * <MobileSimulator 
 *   src="http://localhost:8081" 
 *   title="Caregiver Mobile App"
 * />
 * 
 * // Static screenshots (works in production)
 * <MobileSimulator 
 *   screenshots={[
 *     { src: '/mobile-dashboard.png', label: 'Dashboard' },
 *     { src: '/mobile-visits.png', label: 'Visits' },
 *   ]}
 *   title="Mobile App Preview"
 * />
 * ```
 */

import React, { useState, useEffect, useCallback } from 'react';

export interface MobileScreenshot {
  /** URL of the screenshot image */
  src: string;
  /** Label for the screenshot (shown in navigation) */
  label: string;
  /** Optional description */
  description?: string;
}

export interface MobileSimulatorProps {
  /** URL to embed (typically Expo web dev server) */
  src?: string;
  /** Array of screenshots for static slideshow mode */
  screenshots?: MobileScreenshot[];
  /** Auto-advance slideshow interval in ms (0 to disable) */
  autoPlayInterval?: number;
  /** Title shown above the simulator */
  title?: string;
  /** Device type to simulate */
  device?: 'iphone' | 'android';
  /** Optional children to render instead of iframe */
  children?: React.ReactNode;
  /** Whether to show device chrome (status bar, home indicator) */
  showChrome?: boolean;
  /** Optional className for custom styling */
  className?: string;
  /** Whether simulator is loading */
  isLoading?: boolean;
}

export const MobileSimulator: React.FC<MobileSimulatorProps> = ({
  src,
  screenshots,
  autoPlayInterval = 5000,
  title = 'Mobile App',
  device = 'iphone',
  children,
  showChrome = true,
  className = '',
  isLoading = false,
}) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Handle screenshot navigation
  const goToNextScreenshot = useCallback(() => {
    if (screenshots && screenshots.length > 0) {
      setCurrentScreenshot((prev) => (prev + 1) % screenshots.length);
    }
  }, [screenshots]);

  const goToPrevScreenshot = useCallback(() => {
    if (screenshots && screenshots.length > 0) {
      setCurrentScreenshot((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    }
  }, [screenshots]);

  // Auto-advance slideshow
  useEffect(() => {
    if (screenshots && screenshots.length > 1 && autoPlayInterval > 0 && !isPaused) {
      const interval = setInterval(goToNextScreenshot, autoPlayInterval);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [screenshots, autoPlayInterval, isPaused, goToNextScreenshot]);

  // iPhone 14 Pro dimensions (in logical pixels)
  const width = 393;
  const height = 852;

  const handleIframeLoad = () => {
    setIframeLoaded(true);
    setIframeError(false);
  };

  const handleIframeError = () => {
    setIframeError(true);
    setIframeLoaded(false);
  };

  return (
    <div className={`mobile-simulator ${className}`}>
      {/* Title */}
      {title && (
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {device === 'iphone' ? 'iPhone 14 Pro' : 'Android Device'}
          </p>
        </div>
      )}

      {/* Device Frame */}
      <div 
        className="relative mx-auto bg-black rounded-[3rem] shadow-2xl overflow-hidden"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          padding: showChrome ? '12px' : '0',
        }}
      >
        {/* Device Chrome - Status Bar */}
        {showChrome && (
          <div className="absolute top-0 left-0 right-0 h-12 bg-transparent z-50 flex items-center justify-between px-6 text-white text-xs">
            <div className="flex items-center gap-1">
              <span>9:41</span>
            </div>
            <div className="w-24 h-6 bg-black rounded-full" /> {/* Dynamic Island */}
            <div className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M17 10v1.126c.367.095.714.24 1.032.428l.796-.797 1.415 1.415-.797.796c.188.318.333.665.428 1.032H21v2h-1.126c-.095.367-.24.714-.428 1.032l.797.796-1.415 1.415-.796-.797a4.993 4.993 0 01-1.032.428V20h-2v-1.126a4.993 4.993 0 01-1.032-.428l-.796.797-1.415-1.415.797-.796A4.993 4.993 0 0110.126 16H9v-2h1.126c.095-.367.24-.714.428-1.032l-.797-.796 1.415-1.415.796.797A4.993 4.993 0 0113 10.126V9h2v1.126a4.993 4.993 0 011.032.428l.796-.797 1.415 1.415-.797.796c.188.318.333.665.428 1.032H19v-2h-2z" clipRule="evenodd" />
              </svg>
              <span className="text-xs">100%</span>
            </div>
          </div>
        )}

        {/* Screen Content */}
        <div 
          className="relative w-full h-full bg-white rounded-[2.5rem] overflow-hidden"
          style={{
            height: showChrome ? `${height - 24}px` : `${height}px`,
          }}
        >
          {/* Loading State */}
          {(isLoading || (!iframeLoaded && src && !iframeError)) && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-blue-600 mb-4" />
                <p className="text-sm text-gray-600">Loading mobile app...</p>
              </div>
            </div>
          )}

          {/* Error State */}
          {iframeError && src && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="text-center p-6">
                <div className="text-4xl mb-4">📱</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  Mobile App Not Running
                </h4>
                <p className="text-sm text-gray-600 mb-4">
                  Start the mobile dev server:
                </p>
                <code className="block bg-gray-800 text-white px-4 py-2 rounded text-xs">
                  cd packages/mobile && npm run web
                </code>
              </div>
            </div>
          )}

          {/* Content */}
          {children ? (
            <div className="w-full h-full overflow-auto">
              {children}
            </div>
          ) : screenshots && screenshots.length > 0 ? (
            /* Screenshot slideshow mode */
            <div 
              className="relative w-full h-full"
              onMouseEnter={() => setIsPaused(true)}
              onMouseLeave={() => setIsPaused(false)}
            >
              {/* Current Screenshot */}
              {screenshots[currentScreenshot] && (
                <>
                  <img
                    src={screenshots[currentScreenshot].src}
                    alt={screenshots[currentScreenshot].label}
                    className="w-full h-full object-cover object-top"
                    style={{ 
                      imageRendering: 'auto',
                    }}
                  />
                  
                  {/* Navigation Overlay */}
                  <div className="absolute inset-0 flex items-center justify-between px-2 opacity-0 hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => { e.stopPropagation(); goToPrevScreenshot(); }}
                      className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2 backdrop-blur-sm transition-colors"
                      aria-label="Previous screenshot"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); goToNextScreenshot(); }}
                      className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2 backdrop-blur-sm transition-colors"
                      aria-label="Next screenshot"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Dot Indicators */}
                  {screenshots.length > 1 && (
                    <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
                      {screenshots.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentScreenshot(index)}
                          className={`w-2 h-2 rounded-full transition-all ${
                            index === currentScreenshot 
                              ? 'bg-white w-4' 
                              : 'bg-white/50 hover:bg-white/75'
                          }`}
                          aria-label={`Go to screenshot ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}

                  {/* Current Screen Label */}
                  <div className="absolute top-2 left-0 right-0 flex justify-center">
                    <span className="bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                      {screenshots[currentScreenshot].label}
                    </span>
                  </div>
                </>
              )}
            </div>
          ) : src ? (
            <iframe
              src={src}
              className="w-full h-full border-0"
              title={title}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              sandbox="allow-same-origin allow-scripts allow-forms allow-modals allow-popups"
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-gray-50">
              <div className="text-center p-6">
                <div className="text-4xl mb-4">📱</div>
                <p className="text-sm text-gray-600">
                  No content to display
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Device Chrome - Home Indicator */}
        {showChrome && device === 'iphone' && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
            <div className="w-32 h-1 bg-white rounded-full opacity-50" />
          </div>
        )}
      </div>

      {/* Instructions */}
      {screenshots && screenshots.length > 0 ? (
        <div className="text-center mt-4 text-xs text-gray-500">
          <p>Screenshot preview ({currentScreenshot + 1} of {screenshots.length})</p>
          <p className="mt-1">Hover to pause, click arrows to navigate</p>
        </div>
      ) : src && !iframeError ? (
        <div className="text-center mt-4 text-xs text-gray-500">
          <p>Interactive mobile app simulator</p>
          <p className="mt-1">Connected to: <span className="font-mono">{src}</span></p>
        </div>
      ) : null}
    </div>
  );
};
